import {
  Box,
  FormControlLabel,
  Grid2,
  Radio,
  RadioGroup,
  SxProps,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { SetStateAction } from 'react';
import { AppointmentType as DBAppointmentType } from '@prisma/client';
import {
  Calendar,
  CalendarOff,
  Footprints,
  LucideIcon,
  Presentation,
  Send,
} from 'lucide-react';
import TattooMachineIcon from '../icons/TattooMachineIcon';

interface Props {
  value:
    | (DBAppointmentType | 'TIME_BLOCK' | 'BOOKING_LINK' | 'TOUCH_UP')
    | undefined;
  setFieldValue: (
    value: SetStateAction<
      | (DBAppointmentType | 'TIME_BLOCK' | 'BOOKING_LINK' | 'TOUCH_UP')
      | undefined
    >,
  ) => void;
  hiddenOptions?: ('WALK_IN' | 'BOOKING_LINK')[];
}

const OPTION_SX: SxProps<Theme> = {
  ':hover': {
    borderColor: theme => theme.palette.common.black,
  },
  transition: 'border-color 0.3s',
};

export default function AppointmentType({
  value,
  setFieldValue,
  hiddenOptions,
}: Props) {
  const isDesktop = useMediaQuery('(min-width:900px)');

  function labelMaker(method: string) {
    let Icon: LucideIcon | typeof TattooMachineIcon = Calendar;
    let labelName = '';
    let labelDesc = '';

    switch (method) {
      case 'NEW_APPOINTMENT':
        Icon = Calendar;
        labelName = 'Add an Appointment';
        labelDesc = 'Schedule an appointment with a client';
        break;
      case 'WALK_IN':
        Icon = Footprints;
        labelName = 'Add a Walk-In';
        labelDesc = 'Add a walk-in customer';
        break;
      case 'CONSULTATION':
        Icon = Presentation;
        labelName = 'Add a Consultation';
        labelDesc = 'Schedule a consultation with a client';
        break;
      case 'TOUCH_UP':
        Icon = TattooMachineIcon;
        labelName = 'Add a free touch-up';
        labelDesc = 'Add a free touch-up appointment';
        break;
      case 'TIME_BLOCK':
        Icon = CalendarOff;
        labelName = 'Add a time block';
        labelDesc = 'Block off time in your calendar';
        break;
      case 'BOOKING_LINK':
        Icon = Send;
        labelName = 'Send booking link';
        labelDesc = 'Send a booking link to a client';
        break;
      default:
        labelName = '';
    }

    return (
      <Box className="appointment-type-label">
        <Icon
          size={75}
          strokeWidth={1.75}
          color={value === method ? 'var(--primary)' : 'var(--darkGrey)'}
        />
        <div className="appointment-type-label-text">
          <Typography variant="h3" align="center">
            {labelName}
          </Typography>
          <Typography variant="body2" align="center">
            {labelDesc}
          </Typography>
        </div>
      </Box>
    );
  }

  return (
    <Box
      className="input-group"
      style={{
        display: 'flex',
        alignItems: isDesktop ? 'flex-start' : 'center',
      }}>
      <Typography variant="h2">What do you want to create?</Typography>
      <RadioGroup
        aria-labelledby="radio-appointment-type"
        name="appointmentType"
        id="appointment-type-radio-group"
        value={value}
        onChange={(_event, newVal) =>
          setFieldValue(newVal as DBAppointmentType)
        }>
        <Grid2 container spacing={2.5} columns={12}>
          {!hiddenOptions?.includes('WALK_IN') && (
            <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
              <FormControlLabel
                value="WALK_IN"
                control={
                  <Radio inputProps={{ className: 'radio-appointment-type' }} />
                }
                label={labelMaker('WALK_IN')}
                className={`appointment-type-label ${
                  value === 'WALK_IN' ? 'checked' : ''
                }`}
                sx={OPTION_SX}
              />
            </Grid2>
          )}
          <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
            <FormControlLabel
              value="NEW_APPOINTMENT"
              control={
                <Radio inputProps={{ className: 'appointment-type-method' }} />
              }
              label={labelMaker('NEW_APPOINTMENT')}
              className={`appointment-type-label ${
                value === 'NEW_APPOINTMENT' ? 'checked' : ''
              }`}
              sx={OPTION_SX}
            />
          </Grid2>
          {!hiddenOptions?.includes('BOOKING_LINK') && (
            <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
              <FormControlLabel
                value="BOOKING_LINK"
                control={
                  <Radio
                    inputProps={{ className: 'appointment-type-method' }}
                  />
                }
                label={labelMaker('BOOKING_LINK')}
                className={`appointment-type-label ${
                  value === 'BOOKING_LINK' ? 'checked' : ''
                }`}
                sx={OPTION_SX}
              />
            </Grid2>
          )}
          <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
            <FormControlLabel
              value="CONSULTATION"
              control={
                <Radio inputProps={{ className: 'appointment-type-method' }} />
              }
              label={labelMaker('CONSULTATION')}
              className={`appointment-type-label ${
                value === 'CONSULTATION' ? 'checked' : ''
              }`}
              sx={OPTION_SX}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
            <FormControlLabel
              value="TOUCH_UP"
              control={
                <Radio inputProps={{ className: 'appointment-type-method' }} />
              }
              label={labelMaker('TOUCH_UP')}
              className={`appointment-type-label ${
                value === 'TOUCH_UP' ? 'checked' : ''
              }`}
              sx={OPTION_SX}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
            <FormControlLabel
              value="TIME_BLOCK"
              control={
                <Radio inputProps={{ className: 'appointment-type-method' }} />
              }
              label={labelMaker('TIME_BLOCK')}
              className={`appointment-type-label ${
                value === 'TIME_BLOCK' ? 'checked' : ''
              }`}
              sx={OPTION_SX}
            />
          </Grid2>
        </Grid2>
      </RadioGroup>
    </Box>
  );
}
